"use client";

import { useState, useEffect } from "react";
import { Link } from "@/i18n/routing";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { BUILDER_COMING_SOON } from "@/lib/constants";
import {
  Lock,
  Truck,
  Gem,
  Phone,
  Mail,
  MapPin,
  Instagram,
  Facebook,
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { formatPrice } from "@/lib/utils";
import { trackEvent } from "@/lib/track";
import { phoneToTelHref } from "@/lib/phone";
import { readClientScope, getActiveScope, type QuickLinkScope } from "@/lib/quick-link-scope";

export default function Footer() {
  const pathname = usePathname();
  const [scope, setScope] = useState<QuickLinkScope | null>(null);
  const { voicePhone, supportEmail } = useSiteConfig();
  useEffect(() => {
    // Only apply the scope when we're actually inside the scoped
    // flow — otherwise the cookie sticks around and the public
    // homepage renders the "Private link" footer (bug 2026-04-29).
    setScope(getActiveScope(readClientScope(), pathname));
  }, [pathname]);

  if (scope) {
    // Stripped-down footer for scoped sessions — no catalog links that
    // would pull the customer out of their private link.
    return (
      <footer className="border-t border-brand-dark/5 bg-brand-cream/30 py-10 px-6">
        <div className="container-luxury flex flex-col items-center gap-3 text-center">
          <Image
            src="/images/logo.png?v=2"
            alt="Rivky Wigs"
            width={100}
            height={50}
            className="h-8 w-auto opacity-70"
          />
          <p className="text-xs text-brand-muted">
            Private link &middot; Curated for you
          </p>
          <div className="flex items-center gap-4 text-xs text-brand-muted">
            <a
              href={phoneToTelHref(voicePhone)}
              className="hover:text-brand-dark transition-colors"
            >
              {voicePhone}
            </a>
            <span>&middot;</span>
            <a
              href={`mailto:${supportEmail}`}
              className="hover:text-brand-dark transition-colors"
            >
              {supportEmail}
            </a>
          </div>
        </div>
      </footer>
    );
  }

  return <FullFooter />;
}

function FullFooter() {
  const t = useTranslations("footer");
  const tn = useTranslations("nav");
  const tc = useTranslations("common");
  const { name: themeName } = useTheme();
  const { freeShippingThreshold, voicePhone, supportEmail } = useSiteConfig();

  const [footerEmail, setFooterEmail] = useState("");
  const [footerStatus, setFooterStatus] = useState<
    "idle" | "loading" | "success"
  >("idle");

  const quickLinks = [
    { label: t("shopAll"), href: "/wigs" },
    { label: tn("accessories"), href: "/accessories" },
    // Builder hidden while it's "Coming Soon" — flip the flag in
    // lib/constants when it's ready.
    ...(BUILDER_COMING_SOON ? [] : [{ label: t("customBuilder"), href: "/builder" }]),
    { label: t("giftCards"), href: "/gift-cards" },
    { label: tn("services"), href: "/book" },
    { label: t("about"), href: "/about" },
  ];

  const helpLinks = [
    { label: t("trackOrder"), href: "/order-lookup" },
    { label: t("faq"), href: "/faq" },
    { label: t("shippingReturns"), href: "/shipping" },
    { label: t("consultation"), href: "/book" },
    { label: t("termsOfService"), href: "/terms" },
    { label: t("privacyPolicy"), href: "/privacy" },
    { label: t("smsTerms"), href: "/sms-terms" },
    { label: "Become a Reseller", href: "/become-a-reseller" },
  ];

  const trustBadges = [
    { icon: Lock, label: t("secureCheckout") },
    { icon: Truck, label: t("freeShippingBadge", { amount: formatPrice(freeShippingThreshold) }) },
    { icon: Gem, label: t("handcraftedQuality") },
  ];

  return (
    <footer className="bg-brand-dark text-white/80" aria-label={t("siteFooter")}>
      {/* Trust badges bar */}
      <div className="border-b border-white/[0.06]">
        <div className="container-luxury py-8 sm:py-10">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8">
            {trustBadges.map((badge, index) => (
              <div
                key={badge.label}
                className="group flex flex-col items-center text-center gap-3 cursor-default"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-10 h-10 rounded-full bg-brand-gold/[0.08] flex items-center justify-center transition-all duration-300 group-hover:bg-brand-gold/[0.15] group-hover:scale-105">
                  <badge.icon className="w-[18px] h-[18px] text-brand-gold transition-colors duration-300" />
                </div>
                <span className="text-[11px] sm:text-xs font-medium tracking-[0.15em] text-white/70 uppercase transition-colors duration-300 group-hover:text-white/90">
                  {badge.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="container-luxury py-16 sm:py-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-14">
          {/* Column 1: Brand + Contact Info */}
          <div
            className="sm:col-span-2 lg:col-span-1 animate-footer-rise"
            style={{ animationDelay: "0ms" }}
          >
            <Link
              href="/"
              className="inline-block hover:opacity-80 transition-opacity duration-300"
            >
              <Image
                src="/images/logo.png?v=2"
                alt="Rivky Wigs"
                width={120}
                height={60}
                className="h-12 w-auto"
              />
            </Link>
            <p className="mt-5 text-sm text-white/60 leading-relaxed max-w-xs">
              {t("brandDescription")}
            </p>

            {/* Contact Info */}
            <div className="mt-8 space-y-4">
              <a
                href={phoneToTelHref(voicePhone)}
                className="flex items-center gap-3 text-sm text-white/60 hover:text-brand-gold transition-all duration-300 group"
              >
                <Phone className="w-4 h-4 text-brand-gold/60 group-hover:text-brand-gold group-hover:scale-110 transition-all duration-300" />{voicePhone}</a>
              <a
                href={`mailto:${supportEmail}`}
                className="flex items-center gap-3 text-sm text-white/60 hover:text-brand-gold transition-all duration-300 group"
              >
                <Mail className="w-4 h-4 text-brand-gold/60 group-hover:text-brand-gold group-hover:scale-110 transition-all duration-300" />
                {supportEmail}
              </a>
              <div className="flex items-center gap-3 text-sm text-white/60 group/loc cursor-default">
                <MapPin className="w-4 h-4 text-brand-gold/60 group-hover/loc:text-brand-gold group-hover/loc:scale-110 transition-all duration-300" />
                <span className="transition-colors duration-300 group-hover/loc:text-white/60">Toms River, NJ</span>
              </div>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div
            className="animate-footer-rise"
            style={{ animationDelay: "120ms" }}
          >
            <h4 className="font-display text-sm tracking-[0.15em] uppercase text-white mb-8">
              {t("quickLinks")}
            </h4>
            <ul className="space-y-3.5">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    onClick={() => trackEvent("footer_link_click", { link: link.label, destination: link.href })}
                    className="footer-link text-sm text-white/60 leading-relaxed hover:text-brand-gold transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 rounded-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Help & Legal */}
          <div
            className="animate-footer-rise"
            style={{ animationDelay: "240ms" }}
          >
            <h4 className="font-display text-sm tracking-[0.15em] uppercase text-white mb-8">
              {t("help")}
            </h4>
            <ul className="space-y-3.5">
              {helpLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    onClick={() => trackEvent("footer_link_click", { link: link.label, destination: link.href })}
                    className="footer-link text-sm text-white/60 leading-relaxed hover:text-brand-gold transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 rounded-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Social, Newsletter */}
          <div
            className="animate-footer-rise"
            style={{ animationDelay: "360ms" }}
          >
            <h4 className="font-display text-sm tracking-[0.15em] uppercase text-white mb-8">
              {t("followUs")}
            </h4>
            <div className="flex items-center gap-3.5 mb-10">
              <a
                href="https://instagram.com/rivky_wigs"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/[0.06] flex items-center justify-center text-white/60 hover:text-brand-gold hover:border-brand-gold/40 hover:bg-brand-gold/[0.08] hover:scale-105 transition-all duration-300"
                aria-label={t("socialInstagram")}
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://facebook.com/rwwigs"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/[0.06] flex items-center justify-center text-white/60 hover:text-brand-gold hover:border-brand-gold/40 hover:bg-brand-gold/[0.08] hover:scale-105 transition-all duration-300"
                aria-label={t("socialFacebook")}
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://tiktok.com/@rwwigs"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/[0.06] flex items-center justify-center text-white/60 hover:text-brand-gold hover:border-brand-gold/40 hover:bg-brand-gold/[0.08] hover:scale-105 transition-all duration-300"
                aria-label={t("socialTikTok")}
              >
                {/* TikTok icon (Lucide doesn't include it) */}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-4 h-4"
                >
                  <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
                </svg>
              </a>
            </div>

            <h4 className="font-display text-sm tracking-[0.15em] uppercase text-white mb-8">
              {t("newsletter")}
            </h4>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!footerEmail || footerStatus === "loading") return;
                setFooterStatus("loading");
                try {
                  const res = await fetch("/api/newsletter/subscribe", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      email: footerEmail,
                      source: "footer",
                    }),
                  });
                  if (!res.ok) throw new Error();
                  setFooterStatus("success");
                  setFooterEmail("");
                  trackEvent("newsletter_subscribe", { source: "footer" });
                  setTimeout(() => setFooterStatus("idle"), 3000);
                } catch {
                  setFooterStatus("idle");
                }
              }}
              className="flex flex-col sm:flex-row"
            >
              <input
                type="email"
                value={footerEmail}
                onChange={(e) => setFooterEmail(e.target.value)}
                placeholder={t("emailPlaceholder")}
                className="flex-1 min-w-0 bg-white/[0.05] border border-white/[0.06] sm:border-e-0 px-5 py-3.5 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-brand-gold/50 focus:bg-white/[0.08] transition-all duration-300"
                aria-label={tc("emailForNewsletter")}
                disabled={footerStatus === "loading"}
              />
              <button
                type="submit"
                disabled={
                  footerStatus === "loading" || footerStatus === "success"
                }
                className="px-6 py-3.5 bg-brand-gold text-white text-[11px] font-medium tracking-[0.15em] uppercase hover:bg-brand-gold-dark active:scale-[0.98] transition-all duration-300 whitespace-nowrap disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold-light focus-visible:ring-offset-2 focus-visible:ring-offset-brand-dark"
              >
                {footerStatus === "success"
                  ? t("subscribed")
                  : footerStatus === "loading"
                    ? t("subscribing")
                    : t("subscribe")}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/[0.06]">
        <div className="container-luxury py-7 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/50 tracking-wide">
            &copy;{" "}
            {t("allRightsReserved", { year: new Date().getFullYear() })}
          </p>

          {/* Payment method SVG icons */}
          <div className="flex items-center gap-3.5">
            {/* Visa */}
            <svg
              viewBox="0 0 48 32"
              className="h-6 w-auto text-white/30 hover:text-white/60 transition-colors duration-300"
              fill="currentColor"
              role="img"
              aria-label={t("payVisa")}
            >
              <rect
                x="0.5"
                y="0.5"
                width="47"
                height="31"
                rx="4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              />
              <text
                x="24"
                y="20"
                textAnchor="middle"
                fontSize="12"
                fontWeight="bold"
                fontStyle="italic"
                fill="currentColor"
                fontFamily="sans-serif"
              >
                VISA
              </text>
            </svg>

            {/* Mastercard */}
            <svg
              viewBox="0 0 48 32"
              className="h-6 w-auto text-white/30 hover:text-white/60 transition-colors duration-300"
              role="img"
              aria-label={t("payMastercard")}
            >
              <rect
                x="0.5"
                y="0.5"
                width="47"
                height="31"
                rx="4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              />
              <circle cx="19" cy="16" r="7" fill="#EB001B" opacity="0.7" />
              <circle cx="29" cy="16" r="7" fill="#F79E1B" opacity="0.7" />
            </svg>

            {/* Amex */}
            <svg
              viewBox="0 0 48 32"
              className="h-6 w-auto text-white/30 hover:text-white/60 transition-colors duration-300"
              fill="currentColor"
              role="img"
              aria-label={t("payAmex")}
            >
              <rect
                x="0.5"
                y="0.5"
                width="47"
                height="31"
                rx="4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              />
              <text
                x="24"
                y="18"
                textAnchor="middle"
                fontSize="7"
                fontWeight="bold"
                fill="currentColor"
                fontFamily="sans-serif"
              >
                AMEX
              </text>
            </svg>

            {/* Apple Pay */}
            <svg
              viewBox="0 0 48 32"
              className="h-6 w-auto text-white/30 hover:text-white/60 transition-colors duration-300"
              fill="currentColor"
              role="img"
              aria-label={t("payApplePay")}
            >
              <rect
                x="0.5"
                y="0.5"
                width="47"
                height="31"
                rx="4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              />
              <text
                x="24"
                y="19"
                textAnchor="middle"
                fontSize="8"
                fontWeight="500"
                fill="currentColor"
                fontFamily="sans-serif"
              >
                Pay
              </text>
            </svg>
          </div>
        </div>
        {/* Theme indicator for bug reporting — dev only */}
        {process.env["NODE_ENV"] !== "production" && (
          <div className="container-luxury flex justify-center pb-4">
            <span className="text-[10px] opacity-20 tracking-wider uppercase">
              Theme: {themeName}
            </span>
          </div>
        )}
      </div>
    </footer>
  );
}
